import React from "react";
import { Box, Group, Stack, Text, ThemeIcon } from "@mantine/core";
import { IconCheck, IconCircleDot, IconClock } from "@tabler/icons-react";

const STEPS = [
  { status: 0, stampKey: "assigned_at", label: "Assigned" },
  { status: 1, stampKey: "started_at", label: "Started" },
  { status: 2, stampKey: "submitted_at", label: "Submitted" },
  { status: 3, stampKey: "scored_at", label: "Scored" },
  { status: 4, stampKey: "processed_at", label: "Processed" },
];

function formatLocalStamp(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString();
}

function stepState(step, currentStatus) {
  if (currentStatus > step.status) return "done";
  if (currentStatus === step.status) return "current";
  if (currentStatus + 1 === step.status) return "next";
  return "waiting";
}

function stepDetail(state, at) {
  if (at) return at;
  if (state === "done") return "Completed";
  if (state === "current") return "Current step";
  if (state === "next") return "Up next";
  return "Waiting";
}

function connectorColor(fromStatus, currentStatus) {
  if (currentStatus > fromStatus) return "var(--mantine-color-green-5)";
  if (currentStatus === fromStatus) return "var(--mantine-color-blue-4)";
  return "var(--mantine-color-gray-3)";
}

export default function AssessmentActivityTimeline({ assessment }) {
  const currentStatus = Number(assessment?.activity_status);

  return (
    <Box mt="sm" style={{ overflowX: "auto" }}>
      <Group gap={0} wrap="nowrap" align="flex-start" justify="space-between">
        {STEPS.map((step, index) => {
          const state = stepState(step, currentStatus);
          const at = formatLocalStamp(assessment?.[step.stampKey]);
          const color =
            state === "done" ? "green" : state === "current" ? "blue" : "gray";
          const bullet =
            state === "done" ? (
              <IconCheck size={14} />
            ) : state === "current" ? (
              <IconCircleDot size={14} />
            ) : (
              <IconClock size={14} />
            );

          return (
            <React.Fragment key={step.stampKey}>
              <Stack
                gap={6}
                align="center"
                ta="center"
                style={{ flex: "1 1 0", minWidth: 110 }}
              >
                <ThemeIcon color={color} variant="light" radius="xl" size={28}>
                  {bullet}
                </ThemeIcon>
                <Text fw={600} size="sm">
                  {step.label}
                  {state === "current" ? " · now" : ""}
                  {state === "next" ? " · up next" : ""}
                </Text>
                <Text size="xs" c={state === "waiting" ? "dimmed" : undefined} lineClamp={2}>
                  {stepDetail(state, at)}
                </Text>
              </Stack>
              {index < STEPS.length - 1 && (
                <Box
                  mt={13}
                  style={{
                    flex: "0.4 1 24px",
                    minWidth: 16,
                    height: 3,
                    borderRadius: 99,
                    background: connectorColor(step.status, currentStatus),
                    backgroundImage:
                      currentStatus === step.status
                        ? "repeating-linear-gradient(90deg, var(--mantine-color-blue-4) 0 6px, transparent 6px 10px)"
                        : undefined,
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </Group>
    </Box>
  );
}
